import { useState, useEffect, useCallback } from 'react';
import supabase, { orgId } from '../supabaseClient';

export default function useSteamUpload() {
  const [uploads, setUploads]     = useState([]);
  const [mappings, setMappings]   = useState({}); // { productName: { type, modelGame, modelDlcId } }
  const [monthlyUnits, setMonthlyUnits] = useState({}); // { 'YYYY-MM': { gameName: { netUnits, rrp, avgPrice, regionalDiscountPct } } }
  const [products, setProducts]   = useState([]); // discovered product names
  const [loading, setLoading]     = useState(true);

  const load = useCallback(async () => {
    const [{ data: ups }, { data: maps }, { data: mu }] = await Promise.all([
      supabase.from('steam_uploads').select('*').eq('org_id', orgId).order('uploaded_at', { ascending: false }).limit(20),
      supabase.from('steam_product_mappings').select('*').eq('org_id', orgId),
      supabase.from('steam_monthly_units').select('*').eq('org_id', orgId).order('month_key'),
    ]);

    setUploads(ups || []);

    const mapObj = {};
    (maps || []).forEach(m => {
      mapObj[m.product_name] = { type: m.mapping_type, modelGame: m.model_game, modelDlcId: m.model_dlc_id };
    });
    setMappings(mapObj);

    // Build monthlyUnits lookup
    const muObj = {};
    (mu || []).forEach(r => {
      if (!muObj[r.month_key]) muObj[r.month_key] = {};
      muObj[r.month_key][r.model_game] = {
        netUnits: r.net_units,
        rrpUsd: r.rrp_usd,
        avgPriceUsd: r.avg_price_usd,
        regionalDiscountPct: r.regional_discount_pct,
      };
    });
    setMonthlyUnits(muObj);

    // Get unique products from actuals
    const { data: prods } = await supabase
      .from('steam_actuals')
      .select('product_name, game_name')
      .eq('org_id', orgId);
    const unique = [];
    const seen = new Set();
    (prods || []).forEach(p => {
      if (!seen.has(p.product_name)) {
        seen.add(p.product_name);
        unique.push({ product_name: p.product_name, game_name: p.game_name });
      }
    });
    setProducts(unique);
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const parseSteamCSV = useCallback((text) => {
    const lines = text.split('\n');
    // Find header row (contains 'Date')
    let headerIdx = -1;
    for (let i = 0; i < Math.min(5, lines.length); i++) {
      if (lines[i].includes('Date') && lines[i].includes('Product Name')) {
        headerIdx = i; break;
      }
    }
    if (headerIdx === -1) throw new Error('Could not find header row in Steam CSV');

    const headers = lines[headerIdx].split(',').map(h => h.trim().replace(/^"|"$/g, ''));
    const rows = [];

    for (let i = headerIdx + 1; i < lines.length; i++) {
      const line = lines[i].trim();
      if (!line) continue;

      // Handle quoted CSV fields
      const vals = [];
      let cur = '', inQ = false;
      for (let c = 0; c < line.length; c++) {
        if (line[c] === '"') { inQ = !inQ; }
        else if (line[c] === ',' && !inQ) { vals.push(cur.trim()); cur = ''; }
        else cur += line[c];
      }
      vals.push(cur.trim());

      const row = {};
      headers.forEach((h, idx) => { row[h] = vals[idx] || ''; });

      const dateStr = row['Date'];
      if (!dateStr || dateStr === 'Date') continue;

      // Parse DD/MM/YYYY
      const parts = dateStr.split('/');
      if (parts.length !== 3) continue;
      const monthKey = `${parts[2]}-${parts[1].padStart(2, '0')}`;

      rows.push({
        month_key: monthKey,
        product_name: row['Product Name'] || '',
        game_name: row['Game'] || '',
        platform: row['Platform'] || '',
        country_code: row['Country Code'] || '',
        currency: row['Currency'] || 'USD',
        gross_units: parseInt(row['Gross Units Sold']) || 0,
        chargebacks: parseInt(row['Chargeback/Returns']) || 0,
        net_units: parseInt(row['Net Units Sold']) || 0,
        base_price: parseFloat(row['Base Price']) || 0,
        sale_price: parseFloat(row['Sale Price']) || 0,
        net_sales_usd: parseFloat(row['Net Steam Sales (USD)']) || 0,
      });
    }
    return rows;
  }, []);

  const computeMonthlyAggregates = useCallback((rows, currentMappings, uploadId) => {
    // Group by month + model_game
    const agg = {}; // { 'YYYY-MM|modelGame': { netUnits, usRows, allRows } }

    rows.forEach(row => {
      const mapping = currentMappings[row.product_name];
      if (!mapping || mapping.type === 'ignore' || !mapping.modelGame) return;

      const key = `${row.month_key}|${mapping.modelGame}`;
      if (!agg[key]) agg[key] = { month_key: row.month_key, model_game: mapping.modelGame, netUnits: 0, usBasePrices: [], allPricedRows: [] };

      agg[key].netUnits += row.net_units;

      // US RRP
      if (row.country_code === 'US' && row.base_price > 0) {
        agg[key].usBasePrices.push(row.base_price);
      }

      // For regional discount: rows with base_price > 0
      if (row.net_units > 0 && row.net_sales_usd !== 0) {
        agg[key].allPricedRows.push({ base_price: row.base_price, net_units: row.net_units, currency: row.currency, netSalesUsd: row.net_sales_usd });
      }
    });

    return Object.values(agg).map(a => {
      const rrpUsd = a.usBasePrices.length > 0 ? Math.max(...a.usBasePrices) : 0;
      // Avg price = total net sales USD / net units (avoids currency mixing)
      const totalNetSalesUSD = a.allPricedRows.reduce((s, r) => s + (r.netSalesUsd || 0), 0);
      const totalPricedUnits = a.allPricedRows.reduce((s, r) => s + r.net_units, 0);
      const avgPrice = totalPricedUnits > 0 ? totalNetSalesUSD / totalPricedUnits : 0;
      const regionalDiscountPct = rrpUsd > 0 && avgPrice > 0 ? Math.max(0, (1 - avgPrice / rrpUsd) * 100) : 0;

      return {
        org_id: orgId,
        upload_id: uploadId,
        month_key: a.month_key,
        model_game: a.model_game,
        net_units: a.netUnits,
        rrp_usd: rrpUsd,
        avg_price_usd: avgPrice,
        regional_discount_pct: Math.round(regionalDiscountPct * 10) / 10,
      };
    });
  }, []);

  const uploadSteam = useCallback(async (text, filename, currentMappings) => {
    const rows = parseSteamCSV(text);
    if (!rows.length) throw new Error('No data rows found in file');

    const dates = rows.map(r => r.month_key).sort();
    const dateFrom = dates[0];
    const dateTo   = dates[dates.length - 1];

    // Insert upload record
    const { data: uploadRow, error: upErr } = await supabase.from('steam_uploads').insert({
      org_id: orgId, filename, date_range_from: dateFrom + '-01',
      date_range_to: dateTo + '-01', row_count: rows.length,
    }).select('id').single();
    if (upErr) throw upErr;
    const uploadId = uploadRow.id;

    // Insert actuals in batches (using smaller batches for large files)
    const actRows = rows.map(r => ({ ...r, org_id: orgId, upload_id: uploadId }));
    const BATCH = 200;
    for (let i = 0; i < actRows.length; i += BATCH) {
      const { error } = await supabase.from('steam_actuals')
        .insert(actRows.slice(i, i + BATCH));
      if (error) console.error('[steam upload batch]', i, error);
    }

    // Compute and upsert monthly aggregates if mappings exist
    const allMappings = { ...mappings, ...currentMappings };
    const aggs = computeMonthlyAggregates(rows, allMappings, uploadId);
    // Delete existing monthly units for this upload then reinsert
    if (aggs.length > 0) {
      await supabase.from('steam_monthly_units').delete().eq('upload_id', uploadId);
      for (let i = 0; i < aggs.length; i += 200) {
        await supabase.from('steam_monthly_units').insert(aggs.slice(i, i + 200));
      }
    }

    await load();
    return { rowCount: rows.length, months: [...new Set(rows.map(r => r.month_key))].sort(), uploadId };
  }, [parseSteamCSV, computeMonthlyAggregates, mappings, load]);

  const saveMapping = useCallback(async (productName, type, modelGame, modelDlcId) => {
    await supabase.from('steam_product_mappings').upsert(
      { org_id: orgId, product_name: productName, mapping_type: type, model_game: modelGame, model_dlc_id: modelDlcId || null, updated_at: new Date().toISOString() },
      { onConflict: 'org_id,product_name' }
    );
    const next = { ...mappings, [productName]: { type, modelGame, modelDlcId } };
    setMappings(next);

    // Recompute monthly units for all uploads after mapping change
    const { data: allRows } = await supabase.from('steam_actuals').select('*').eq('org_id', orgId);
    if (allRows?.length) {
      // Group by upload_id and recompute
      const byUpload = {};
      allRows.forEach(r => {
        if (!byUpload[r.upload_id]) byUpload[r.upload_id] = [];
        byUpload[r.upload_id].push(r);
      });
      for (const [uid, rows] of Object.entries(byUpload)) {
        const aggs = computeMonthlyAggregates(rows, next, uid);
        if (aggs.length > 0) {
          await supabase.from('steam_monthly_units')
            .upsert(aggs, { onConflict: 'org_id,upload_id,month_key,model_game' });
        }
      }
    }
    await load();
  }, [mappings, computeMonthlyAggregates, load]);

  // Get units for a specific game and month (for Game Assumptions tab)
  const getUnitsForGame = useCallback((modelGame, monthKey) => {
    return monthlyUnits[monthKey]?.[modelGame]?.netUnits ?? null;
  }, [monthlyUnits]);

  const getRRPForGame = useCallback((modelGame, monthKey) => {
    return monthlyUnits[monthKey]?.[modelGame]?.rrpUsd ?? null;
  }, [monthlyUnits]);

  const getRegionalDiscountForGame = useCallback((modelGame, monthKey) => {
    return monthlyUnits[monthKey]?.[modelGame]?.regionalDiscountPct ?? null;
  }, [monthlyUnits]);

  return {
    uploads, mappings, products, monthlyUnits, loading,
    uploadSteam, saveMapping,
    getUnitsForGame, getRRPForGame, getRegionalDiscountForGame,
  };
}
